import React, { useEffect, useState } from 'react';
import {
  Bell, Plus, Pencil, Trash2, X, Save, Loader2, Users,
} from 'lucide-react';
import api from '../../services/api';
import { successAlert, errorAlert, confirmDialog } from '../../utils/alerts';

const emptyForm = {
  title: '',
  message: '',
  is_active: true,
  send_to_all: true,
  recipients: [],
  expires_at: '',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

// Converte ISO do backend para o formato aceito por <input type="datetime-local">
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const NotificationsSettings = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.get('/notifications/');
      setNotifications(res.data.results || res.data);
    } catch {
      errorAlert('Erro ao carregar', 'Não foi possível carregar as notificações.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users/');
      setUsers(res.data.results || res.data);
    } catch {
      // lista de usuários é opcional para exibição; erro tratado no envio
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (n) => {
    setEditing(n);
    setForm({
      title: n.title || '',
      message: n.message || '',
      is_active: n.is_active ?? true,
      send_to_all: n.send_to_all ?? false,
      recipients: (n.recipients_detail || []).map((u) => u.id),
      expires_at: toLocalInput(n.expires_at),
    });
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleRecipient = (id) => {
    setForm((prev) => ({
      ...prev,
      recipients: prev.recipients.includes(id)
        ? prev.recipients.filter((r) => r !== id)
        : [...prev.recipients, id],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      errorAlert('Campos obrigatórios', 'Preencha o título e a mensagem.');
      return;
    }
    if (!form.send_to_all && form.recipients.length === 0) {
      errorAlert('Destinatários', 'Selecione ao menos um usuário ou marque "Enviar para todos".');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        message: form.message,
        is_active: form.is_active,
        send_to_all: form.send_to_all,
        recipients: form.send_to_all ? [] : form.recipients,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      if (editing) {
        await api.patch(`/notifications/${editing.id}/`, payload);
        await successAlert('Notificação atualizada', 'As alterações foram salvas com sucesso.');
      } else {
        await api.post('/notifications/', payload);
        await successAlert('Notificação criada', 'A notificação foi enviada com sucesso.');
      }
      setModalOpen(false);
      fetchNotifications();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Não foi possível salvar a notificação.';
      errorAlert('Erro ao salvar', detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (n) => {
    const confirmed = await confirmDialog({
      title: 'Excluir notificação',
      text: `Deseja realmente excluir a notificação "${n.title}"?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/notifications/${n.id}/`);
      await successAlert('Notificação excluída', 'A notificação foi removida com sucesso.');
      fetchNotifications();
    } catch {
      errorAlert('Erro ao excluir', 'Não foi possível excluir a notificação.');
    }
  };

  const audienceLabel = (n) => {
    if (n.send_to_all) return 'Todos';
    const names = (n.recipients_detail || []).map((u) => u.nome || u.username);
    return names.length > 0 ? names.join(', ') : '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-sm text-gray-500">
          Gerencie os avisos exibidos aos usuários do sistema.
        </p>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus size={16} />
          Nova notificação
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Título</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Público</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ativa</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Expira em</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Criada por</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {notifications.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-400 text-sm">
                    Nenhuma notificação cadastrada.
                  </td>
                </tr>
              ) : (
                notifications.map((n) => (
                  <tr key={n.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{n.title}</div>
                      <div className="text-gray-500 text-xs truncate max-w-xs">{n.message}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <span className="inline-block max-w-xs truncate" title={audienceLabel(n)}>
                        {audienceLabel(n)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {n.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          Ativa
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                          Inativa
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{formatDateTime(n.expires_at)}</td>
                    <td className="px-6 py-4 text-gray-600">{n.created_by_name || '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => openEdit(n)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(n)}
                          className="text-red-500 hover:text-red-700"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de criação/edição */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Bell size={18} className="text-gray-500" />
                {editing ? 'Editar Notificação' : 'Nova Notificação'}
              </h3>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label htmlFor="notif_title" className="block text-sm font-medium text-gray-700 mb-1">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  id="notif_title"
                  name="title"
                  type="text"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Título da notificação"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label htmlFor="notif_message" className="block text-sm font-medium text-gray-700 mb-1">
                  Mensagem <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="notif_message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Digite a mensagem..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 font-medium">Ativa</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="send_to_all"
                    checked={form.send_to_all}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 font-medium">Enviar para todos os usuários</span>
                </label>
              </div>

              {!form.send_to_all && (
                <div>
                  <p className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                    <Users size={14} className="text-gray-400" />
                    Destinatários <span className="text-red-500">*</span>
                  </p>
                  <div className="border border-gray-300 rounded-lg max-h-44 overflow-y-auto divide-y divide-gray-50">
                    {users.length === 0 ? (
                      <p className="px-3 py-3 text-sm text-gray-400">Nenhum usuário disponível.</p>
                    ) : (
                      users.map((u) => (
                        <label
                          key={u.id}
                          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50 select-none"
                        >
                          <input
                            type="checkbox"
                            checked={form.recipients.includes(u.id)}
                            onChange={() => toggleRecipient(u.id)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            {u.nome || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username}
                            <span className="text-gray-400 text-xs ml-1.5">@{u.username}</span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="notif_expires" className="block text-sm font-medium text-gray-700 mb-1">
                  Data de expiração <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <input
                  id="notif_expires"
                  name="expires_at"
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#1e3a5f' }}
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {editing ? 'Salvar Alterações' : 'Criar Notificação'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsSettings;
